/** Set de iconos SVG inline propios (sin webfont, design.md §22.1). */
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export type MobileIconName =
  | 'home'
  | 'add_circle'
  | 'add'
  | 'request_quote'
  | 'menu'
  | 'more_horiz'
  | 'task_alt'
  | 'task'
  | 'assignment_add'
  | 'assignment'
  | 'point_of_sale'
  | 'calculate'
  | 'contacts'
  | 'groups'
  | 'person'
  | 'person_add'
  | 'person_off'
  | 'business'
  | 'business_center'
  | 'store'
  | 'mail'
  | 'lock'
  | 'lock_person'
  | 'visibility'
  | 'visibility_off'
  | 'search'
  | 'search_off'
  | 'error'
  | 'error_outline'
  | 'warning'
  | 'info'
  | 'check_circle'
  | 'refresh'
  | 'sync'
  | 'chevron_right'
  | 'chevron_down'
  | 'arrow_back'
  | 'arrow_forward'
  | 'arrow_downward'
  | 'trending_up'
  | 'trending_down'
  | 'logout'
  | 'save'
  | 'photo_camera'
  | 'photo_library'
  | 'folder_open'
  | 'wifi_off'
  | 'send'
  | 'help'
  | 'swap_horiz'
  | 'swap_vert'
  | 'notifications_active'
  | 'timer'
  | 'timer_off'
  | 'picture_as_pdf'
  | 'cancel'
  | 'inventory_2'
  | 'badge'
  | 'title'
  | 'close'
  | 'schedule'
  | 'calendar_today'
  | 'pie_chart'
  | 'bar_chart'
  | 'block'
  | 'verified'
  | 'fact_check'
  | 'engineering'
  | 'design_services'
  | 'account_balance_wallet'
  | 'payments'
  | 'receipt_long'
  | 'edit_note'
  | 'how_to_reg'
  | 'hourglass_empty'
  | 'pause_circle'
  | 'do_not_disturb'
  | 'celebration'
  | 'inbox'
  | 'circle'
  | 'flag';

type MobileIconProps = {
  name: MobileIconName;
  className?: string;
};

const paths: Record<MobileIconName, ReactNode> = {
  home: (
    <>
      <path d="M4 10.5 12 3.5l8 7" />
      <path d="M6 9.5V20h12V9.5" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  add_circle: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8.5v7M8.5 12h7" />
    </>
  ),
  add: <path d="M12 5.5v13M5.5 12h13" />,
  request_quote: (
    <>
      <path d="M6.5 3.5h11V20h-11z" />
      <path d="M9.5 8h5M9.5 11.5h5M9.5 15h3" />
    </>
  ),
  menu: <path d="M4.5 7h15M4.5 12h15M4.5 17h15" />,
  more_horiz: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  task_alt: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12.3l2.4 2.4 4.6-5.2" />
    </>
  ),
  task: (
    <>
      <path d="M6.5 3.5h11V20h-11z" />
      <path d="M9.5 12.5l1.8 1.8 3.2-3.6" />
    </>
  ),
  assignment_add: (
    <>
      <path d="M7 3.5h7l4 4V20H7z" />
      <path d="M14 3.5V8h4" />
      <path d="M11 12v5M8.5 14.5h5" />
    </>
  ),
  assignment: (
    <>
      <path d="M7 3.5h7l4 4V20H7z" />
      <path d="M14 3.5V8h4" />
      <path d="M9.5 12h5M9.5 15.5h5" />
    </>
  ),
  point_of_sale: (
    <>
      <path d="M4 6.5h16V14H4z" />
      <path d="M4 10.5h16" />
      <path d="M7.5 17.5h4" />
      <path d="M12 14v6M8.5 20.5h7" />
    </>
  ),
  calculate: (
    <>
      <rect x="6" y="3.5" width="12" height="17" rx="2" />
      <path d="M9 7.5h6" />
      <path d="M9 12h6M9 15.5h6" />
      <path d="M12 12v6.5" />
    </>
  ),
  contacts: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19.5c0-3 2.5-4.8 5.5-4.8s5.5 1.8 5.5 4.8" />
      <circle cx="16.5" cy="9.5" r="2.3" />
      <path d="M16 14.7c2 .4 3.5 1.8 3.5 3.8" />
    </>
  ),
  groups: (
    <>
      <circle cx="9" cy="8.8" r="2.8" />
      <path d="M3.8 19c0-2.8 2.3-4.5 5.2-4.5s5.2 1.7 5.2 4.5" />
      <circle cx="16.8" cy="9.5" r="2.1" />
      <path d="M16.3 14.6c2.1.3 3.6 1.7 3.9 3.9" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20c0-3.4 3-5.5 6.5-5.5s6.5 2.1 6.5 5.5" />
    </>
  ),
  person_add: (
    <>
      <circle cx="10" cy="8" r="3.2" />
      <path d="M4 20c0-3.2 2.7-5 6-5 1 0 2 .2 2.8.6" />
      <path d="M18.5 8.5v6M15.5 11.5h6" />
    </>
  ),
  person_off: (
    <>
      <circle cx="10" cy="8" r="3.2" />
      <path d="M4 20c0-3.2 2.7-5 6-5 1.2 0 2.3.3 3.2.8" />
      <path d="M4.5 4.5l15 15" />
    </>
  ),
  business: (
    <>
      <path d="M4.5 20.5V6l7-2.5v17" />
      <path d="M11.5 20.5V9.5l8-2.5v13.5" />
      <path d="M4.5 20.5h15" />
      <path d="M7 9h2M7 12.5h2M7 16h2M14.5 12.5h1.8M14.5 16h1.8" />
    </>
  ),
  business_center: (
    <>
      <rect x="3.5" y="8" width="17" height="11" rx="2" />
      <path d="M9 8V6.5A1.5 1.5 0 0 1 10.5 5h3A1.5 1.5 0 0 1 15 6.5V8" />
      <path d="M3.5 12.5h17" />
    </>
  ),
  store: (
    <>
      <path d="M4.5 9.5 5.5 5h13l1 4.5" />
      <path d="M4.5 9.5h15V20h-15z" />
      <path d="M9.5 20v-5h5v5" />
    </>
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="M4.5 7.5 12 13l7.5-5.5" />
    </>
  ),
  lock: (
    <>
      <rect x="6" y="11" width="12" height="9" rx="2" />
      <path d="M8.5 11V7.8a3.5 3.5 0 0 1 7 0V11" />
    </>
  ),
  lock_person: (
    <>
      <circle cx="10" cy="8" r="3" />
      <path d="M3.5 20c0-2.8 2.4-4.6 5.6-4.9" />
      <rect x="14" y="13" width="7" height="7" rx="1.6" />
      <path d="M16 13v-1.4a1.6 1.6 0 0 1 3.2 0V13" />
    </>
  ),
  visibility: (
    <>
      <path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.6" />
    </>
  ),
  visibility_off: (
    <>
      <path d="M4 6.5C6.5 4.8 9.2 4 12 4c3 0 5.6 1.2 7.5 2.7" />
      <path d="M19.5 17.5C17.5 18.9 14.8 20 12 20c-3 0-5.6-1.2-7.5-2.7" />
      <path d="M4 4l16 16" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.8 15.8 20.5 20.5" />
    </>
  ),
  search_off: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.8 15.8 20.5 20.5" />
      <path d="M4.5 4.5l15 15" />
    </>
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V13" />
      <circle cx="12" cy="16.2" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  error_outline: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V13" />
      <circle cx="12" cy="16.2" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4 21 19.5H3Z" />
      <path d="M12 10v4.5" />
      <circle cx="12" cy="16.8" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11.5V16" />
      <circle cx="12" cy="8.2" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  check_circle: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 12.3l2.4 2.4 4.6-5.2" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M18.5 3.5v4h-4" />
    </>
  ),
  sync: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M18.5 3.5v4h-4" />
    </>
  ),
  chevron_right: <path d="M9.5 6 15.5 12l-6 6" />,
  chevron_down: <path d="M6 9.5 12 15.5l6-6" />,
  arrow_back: (
    <>
      <path d="M19 12H5" />
      <path d="M11 6.5 5.5 12l5.5 5.5" />
    </>
  ),
  arrow_forward: (
    <>
      <path d="M5 12h14" />
      <path d="M13 6.5 18.5 12 13 17.5" />
    </>
  ),
  trending_up: (
    <>
      <path d="M4 16.5 9.5 11l3 3L20 6.5" />
      <path d="M15.5 6.5H20v4.5" />
    </>
  ),
  trending_down: (
    <>
      <path d="M4 7.5 9.5 13l3-3L20 17.5" />
      <path d="M15.5 17.5H20V13" />
    </>
  ),
  arrow_downward: (
    <>
      <path d="M12 5v14" />
      <path d="M6.5 13.5 12 19l5.5-5.5" />
    </>
  ),
  logout: (
    <>
      <path d="M14 4.5H6v15h8" />
      <path d="M10.5 12H20" />
      <path d="M17.5 8.5 21 12l-3.5 3.5" />
    </>
  ),
  save: (
    <>
      <path d="M5.5 3.5h10.5l4.5 4.5v12.5h-15z" />
      <path d="M8.5 3.5V9h6.5V3.5" />
      <path d="M8.5 20.5v-6.5h7v6.5" />
    </>
  ),
  photo_camera: (
    <>
      <path d="M4 8.5h3l1.8-2h6.4l1.8 2h3V19H4z" />
      <circle cx="12" cy="13.5" r="3.2" />
    </>
  ),
  photo_library: (
    <>
      <rect x="3.5" y="6.5" width="17" height="13" rx="2" />
      <circle cx="9" cy="11" r="1.4" />
      <path d="M5.5 18l4.5-4.5 2.8 2.8 2.7-2.7 3 3" />
    </>
  ),
  folder_open: (
    <>
      <path d="M3.5 7h6l2 2.2h9V18h-17z" />
      <path d="M3.5 7v11" />
    </>
  ),
  wifi_off: (
    <>
      <path d="M5 10.5a10 10 0 0 1 3.2-2.2M12.5 6.5A10 10 0 0 1 19 10.5" />
      <path d="M8.3 13.5a5.5 5.5 0 0 1 2.5-1.4M13.7 12.3a5.5 5.5 0 0 1 2 1.2" />
      <circle cx="12" cy="17.5" r="1.2" fill="currentColor" stroke="none" />
      <path d="M4 4l16 16" />
    </>
  ),
  send: (
    <>
      <path d="M21 3.5 11 13.5" />
      <path d="M21 3.5 14.5 21l-3.5-7.5L3.5 10Z" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.3A2.5 2.5 0 1 1 13 11.7c-.7.4-1 .8-1 1.8" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  swap_horiz: (
    <>
      <path d="M7 8.5h12.5" />
      <path d="M16.5 5.5 19.5 8.5l-3 3" />
      <path d="M17 15.5H4.5" />
      <path d="M7.5 12.5 4.5 15.5l3 3" />
    </>
  ),
  swap_vert: (
    <>
      <path d="M8.5 7V19.5" />
      <path d="M5.5 16.5 8.5 19.5l3-3" />
      <path d="M15.5 17V4.5" />
      <path d="M12.5 7.5l3-3 3 3" />
    </>
  ),
  notifications_active: (
    <>
      <path d="M6.5 9.5a5.5 5.5 0 1 1 11 0c0 4.5 1.5 5.5 1.5 5.5H5s1.5-1 1.5-5.5" />
      <path d="M10 18.5a2 2 0 0 0 4 0" />
      <path d="M4.5 4.5 5.8 6M19.5 4.5 18.2 6" />
    </>
  ),
  timer: (
    <>
      <circle cx="12" cy="13.5" r="7" />
      <path d="M12 10v3.8l2.6 1.7" />
      <path d="M9.5 2.5h5" />
    </>
  ),
  timer_off: (
    <>
      <circle cx="12" cy="13.5" r="7" />
      <path d="M12 10v3.8l2.6 1.7" />
      <path d="M9.5 2.5h5" />
      <path d="M4.5 4.5l15 15" />
    </>
  ),
  picture_as_pdf: (
    <>
      <path d="M7 3.5h7l4 4v13H7z" />
      <path d="M14 3.5V8h4" />
      <path d="M10 12.5h4M10 16h4" />
    </>
  ),
  cancel: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" />
    </>
  ),
  inventory_2: (
    <>
      <path d="M3.5 8 12 4l8.5 4v8L12 20l-8.5-4z" />
      <path d="M3.5 8 12 12l8.5-4" />
      <path d="M12 12v8" />
    </>
  ),
  badge: (
    <>
      <rect x="5.5" y="4" width="13" height="16" rx="2" />
      <circle cx="12" cy="10" r="2" />
      <path d="M9 16.5c.4-1.8 1.6-2.6 3-2.6s2.6.8 3 2.6" />
    </>
  ),
  title: (
    <>
      <path d="M5 6.5h14" />
      <path d="M12 6.5v13" />
    </>
  ),
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  schedule: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  calendar_today: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M4 10.5h16" />
      <path d="M8.5 3.5v3M15.5 3.5v3" />
    </>
  ),
  pie_chart: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5V12h8.5" />
    </>
  ),
  bar_chart: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M5.5 20.5v-6M10 20.5V6.5M14.5 20.5v-9M19 20.5V10.5" />
    </>
  ),
  block: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M6 6l12 12" />
    </>
  ),
  verified: (
    <>
      <path d="M12 3.5 19 6.5v5.5c0 4.3-2.9 7-7 8.5-4.1-1.5-7-4.2-7-8.5V6.5Z" />
      <path d="M9.3 12l2 2 3.4-3.8" />
    </>
  ),
  fact_check: (
    <>
      <path d="M6.5 3.5h11V20h-11z" />
      <path d="M9 13.5l1.8 1.8 3.2-3.6" />
      <path d="M9 8h5" />
    </>
  ),
  engineering: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.5M12 18v2.5M3.5 12H6M18 12h2.5M6 6l1.8 1.8M16.2 16.2 18 18M18 6l-1.8 1.8M7.8 16.2 6 18" />
    </>
  ),
  design_services: (
    <>
      <path d="M4 20l1-4L16.5 4.5a1.8 1.8 0 0 1 2.5 2.5Z" />
      <path d="M13.5 7.5l3 3" />
    </>
  ),
  account_balance_wallet: (
    <>
      <path d="M4 8.5h13v10H4z" />
      <path d="M17 11.5h2.5a1 1 0 0 1 0 2H17" />
      <path d="M6 8.5V6h11v2.5" />
    </>
  ),
  payments: (
    <>
      <rect x="3.5" y="6" width="17" height="12.5" rx="2" />
      <path d="M3.5 10.5h17" />
      <path d="M7 14.5h4" />
    </>
  ),
  receipt_long: (
    <>
      <path d="M6.5 3.5h11V20l-2.75-1.8L12 20l-2.75-1.8L6.5 20z" />
      <path d="M9.5 8h5M9.5 11.5h5" />
    </>
  ),
  edit_note: (
    <>
      <path d="M6 3.5h12V20l-3-2-3 2-3-2-3 2z" />
      <path d="M9.5 8.5h5M9.5 12h5" />
    </>
  ),
  how_to_reg: (
    <>
      <circle cx="10" cy="8" r="3.2" />
      <path d="M4 20c0-3.2 2.7-5 6-5 .9 0 1.8.2 2.6.5" />
      <path d="M14.5 14.5l1.8 1.8 3.2-3.6" />
    </>
  ),
  hourglass_empty: (
    <>
      <path d="M6.5 3.5h11M6.5 20.5h11" />
      <path d="M8 3.5c0 4 3 5 4 8.5-1 3.5-4 4.5-4 8.5M16 3.5c0 4-3 5-4 8.5 1 3.5 4 4.5 4 8.5" />
    </>
  ),
  pause_circle: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10 9.5v5M14 9.5v5" />
    </>
  ),
  do_not_disturb: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12h8" />
    </>
  ),
  celebration: (
    <>
      <path d="M5 15 15 5l3 3L8 18H5z" />
      <path d="M15 5l1.5-1.5M18.5 6.5 20 5M17 3.5V2M19.5 9.5H21" />
      <path d="M5 18c-1 1-1.5 2.5-1.5 2.5S5 20 6 19" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.5 13.5V7h17v6.5l-2.5 6h-12z" />
      <path d="M3.5 13.5H9a3 3 0 0 0 6 0h5.5" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="5" />,
  flag: (
    <>
      <path d="M6 21V4" />
      <path d="M6 5h11l-2.5 3.5L17 12H6" />
    </>
  ),
};

export function MobileIcon({ name, className }: MobileIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={clsx('h-6 w-6 shrink-0', className)}
    >
      {paths[name]}
    </svg>
  );
}
